import type React from "react";
import { useSearchParams } from "react-router";

import { EstadoDeError } from "@/components/estados/EstadoDeError";
import { EstadoSinConexion } from "@/components/estados/EstadoSinConexion";
import { RotuloDeFiltro } from "@/components/RotuloDeFiltro";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAccionDemorada } from "@/hooks/useAccionDemorada";
import { useLogDeConsumoDeIa } from "@/hooks/useConsumoDeIa";
import { useIaFunciones } from "@/hooks/useMantenedorDeIa";
import { formatearFechaYHora } from "@/lib/fechas";
import { estadoDeConsulta } from "@/lib/estadoDeConsulta";
import { formatearCostoUsd, formatearTokens } from "@/lib/formatoDeConsumo";

const FILAS_SKELETON = 5;

/**
 * El log de peticiones al proveedor: una fila por llamada, la más reciente
 * primero.
 *
 * SOLO SE MONTA CUANDO SU PESTAÑA ESTÁ ACTIVA (`ConsumoDeIa`), y por eso lee
 * sus filtros de la URL adentro suyo en vez de recibirlos por props: nace y
 * muere con la pestaña, y `useSearchParams` sigue viendo la misma url.
 *
 * SIN RANGO DE FECHAS A PROPÓSITO: `IndexConsumoDeIaRequest` no acota a un
 * mes como el resumen — es el detalle fila por fila, y quien lo abre decide
 * qué recortar. El filtro de fecha del "Resumen" es OTRO alcance y no se
 * comparte acá.
 */
export function LogDeConsumoDeIa() {
  const [params, setParams] = useSearchParams();
  const funciones = useIaFunciones();

  const iaFuncionId = Number(params.get("funcion")) || undefined;
  const modeloFiltro = params.get("modelo") ?? "";
  const pagina = Number(params.get("pagina")) || 1;

  const log = useLogDeConsumoDeIa({ iaFuncionId, modelo: modeloFiltro || undefined, pagina });
  const estado = estadoDeConsulta(log);

  function cambiarFiltro(clave: "funcion" | "modelo", valor: string) {
    const siguiente = new URLSearchParams(params);
    if (valor) siguiente.set(clave, valor);
    else siguiente.delete(clave);
    siguiente.delete("pagina");
    setParams(siguiente, { replace: true });
  }

  const modelo = useAccionDemorada((texto: string) => cambiarFiltro("modelo", texto));

  function irAPagina(evento: React.MouseEvent, n: number) {
    evento.preventDefault();
    const siguiente = new URLSearchParams(params);
    if (n > 1) siguiente.set("pagina", String(n));
    else siguiente.delete("pagina");
    setParams(siguiente, { replace: true });
  }

  function urlDePagina(n: number): string {
    const siguiente = new URLSearchParams(params);
    if (n > 1) siguiente.set("pagina", String(n));
    else siguiente.delete("pagina");
    return `?${siguiente.toString()}`;
  }

  const consumos = log.data?.consumos ?? [];
  const totalPaginas = log.data?.totalPaginas ?? 1;
  const total = log.data?.total ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex w-56 flex-col gap-1.5">
          <RotuloDeFiltro htmlFor="log-ia-funcion">Función</RotuloDeFiltro>
          <NativeSelect
            id="log-ia-funcion"
            value={params.get("funcion") ?? ""}
            onChange={(evento) => cambiarFiltro("funcion", evento.target.value)}
          >
            <option value="">Todas</option>
            {(funciones.data ?? []).map((f) => (
              <option key={f.id} value={f.id}>
                {f.nombre}
              </option>
            ))}
          </NativeSelect>
        </div>

        <div className="flex w-56 flex-col gap-1.5">
          <RotuloDeFiltro htmlFor="log-modelo">Modelo</RotuloDeFiltro>
          <Input
            id="log-modelo"
            defaultValue={modeloFiltro}
            placeholder="Todos"
            autoComplete="off"
            onChange={(evento) => modelo(evento.target.value)}
          />
        </div>

        <p className="text-ink-hint ml-auto text-xs">
          {total === 0 ? "Sin peticiones" : `${formatearTokens(total)} peticiones`}
        </p>
      </div>

      {estado.sinConexion ? (
        <EstadoSinConexion que="el log de consumo de IA" />
      ) : estado.hayError ? (
        <EstadoDeError
          titulo="No pudimos cargar el log"
          error={log.error}
          onReintentar={() => log.refetch()}
          reintentando={estado.recargando}
        />
      ) : estado.cargando ? (
        <div className="space-y-2">
          {Array.from({ length: FILAS_SKELETON }).map((_, i) => (
            <Skeleton
              key={i}
              role="status"
              aria-label="Cargando el log de consumo de IA"
              className="h-11 w-full rounded-md"
            />
          ))}
        </div>
      ) : consumos.length === 0 ? (
        <Empty className="rounded-xl border">
          <EmptyHeader>
            <EmptyTitle>Ninguna petición con estos filtros</EmptyTitle>
            <EmptyDescription>
              Prueba con otra función o modelo, o quítalos para ver todo el historial.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className="bg-card overflow-hidden rounded-xl border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha y hora</TableHead>
                <TableHead>Función</TableHead>
                <TableHead>Origen</TableHead>
                <TableHead>Modelo</TableHead>
                <TableHead>Usuario</TableHead>
                <TableHead>Informe</TableHead>
                <TableHead className="text-right">Entrada</TableHead>
                <TableHead className="text-right">Salida</TableHead>
                <TableHead className="text-right">Caché</TableHead>
                <TableHead className="text-right">Razonamiento</TableHead>
                <TableHead className="text-right">Costo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {consumos.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-mono text-xs whitespace-nowrap">
                    {formatearFechaYHora(c.ocurrido_en)}
                  </TableCell>
                  <TableCell>{c.funcion_nombre ?? "—"}</TableCell>
                  <TableCell className="text-ink-hint font-mono text-xs">{c.origen}</TableCell>
                  <TableCell className="font-mono text-xs">{c.modelo}</TableCell>
                  <TableCell>{c.user_nombre ?? "—"}</TableCell>
                  <TableCell>{c.informe_numero ?? "—"}</TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatearTokens(c.tokens_entrada)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatearTokens(c.tokens_salida)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatearTokens(c.tokens_cache_lectura)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatearTokens(c.tokens_razonamiento)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs font-bold">
                    {formatearCostoUsd(c.costo_usd)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {totalPaginas > 1 && (
        <Pagination className="mx-0 w-auto justify-end">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href={urlDePagina(pagina - 1)}
                aria-label="Página anterior"
                aria-disabled={pagina === 1}
                className={pagina === 1 ? "pointer-events-none opacity-50" : undefined}
                onClick={(evento) => irAPagina(evento, pagina - 1)}
              />
            </PaginationItem>

            {Array.from({ length: totalPaginas }, (_, i) => i + 1).map((n) => (
              <PaginationItem key={n}>
                <PaginationLink
                  href={urlDePagina(n)}
                  isActive={n === pagina}
                  onClick={(evento) => irAPagina(evento, n)}
                >
                  {n}
                </PaginationLink>
              </PaginationItem>
            ))}

            <PaginationItem>
              <PaginationNext
                href={urlDePagina(pagina + 1)}
                aria-label="Página siguiente"
                aria-disabled={pagina === totalPaginas}
                className={pagina === totalPaginas ? "pointer-events-none opacity-50" : undefined}
                onClick={(evento) => irAPagina(evento, pagina + 1)}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
}
